import React, { useEffect, useState } from 'react';
import clinicService from '../../../../../services/clinic.service';
import { useParams } from 'react-router-dom';
import { Pagination, Alert, Collapse } from 'react-bootstrap';
import Loading from '../../../../common/Loading';
import Swal from '../../../../helpers/swal';
function HistoryTab() {
    const { clinicId } = useParams();
    const [historyList, setHistoryList] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expandedRows, setExpandedRows] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const pageSizeOptions = [10, 20, 50];

    useEffect(() => {
        const getHistory = async () => {
            try {
                setLoading(true);
                setError(null);

                const pageNumber = currentPage - 1;
                const res = await clinicService.getHistory(clinicId, pageNumber, itemsPerPage);

                setHistoryList(res.data.content);
                setTotalElements(res.data.totalElements);
            } catch (err) {
                console.error(err);
                setError('Failed to load history data. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        getHistory();
    }, [clinicId, currentPage, itemsPerPage]);

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        Swal.fire({
            icon: 'success',
            title: 'Copied!',
            text: 'Sync ID copied to clipboard',
            timer: 1500,
            showConfirmButton: false,
            position: 'top-end',
            toast: true,
        });
    };

    const toggleRowExpand = (id) => {
        setExpandedRows((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    const getStatusBadgeClass = (result) =>
        result === 'SUCCESS'
            ? 'clinic-history-status-badge clinic-history-status-badge--success'
            : 'clinic-history-status-badge clinic-history-status-badge--error';

    const totalPages = Math.ceil(totalElements / itemsPerPage);
    const startItem = historyList.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
    const endItem = Math.min(currentPage * itemsPerPage, totalElements);

    let paginationItems = [];
    for (let number = 1; number <= totalPages; number++) {
        paginationItems.push(
            <Pagination.Item key={number} active={number === currentPage} onClick={() => setCurrentPage(number)}>
                {number}
            </Pagination.Item>
        );
    }

    return (
        <>
            <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
                <div>
                    <h5 className="clinic-history__title mb-1">
                        <i className="bi bi-clock-history text-primary me-1"></i>
                        Sync History
                        <span className="clinic-history__count">
                            ({totalElements} record{totalElements !== 1 ? 's' : ''})
                        </span>
                    </h5>
                    <p className="text-muted small mb-0">PMS sync results and notes for this clinic.</p>
                </div>
            </div>

            {error && (
                <Alert variant="warning" className="border-0 shadow-sm mb-3">
                    {error}
                </Alert>
            )}

            <div className="clinic-history-toolbar">
                <div className="d-flex align-items-center gap-2">
                    <span>Show</span>
                    <select
                        className="form-select form-select-sm clinic-history-page-size"
                        value={itemsPerPage}
                        onChange={handleItemsPerPageChange}
                        disabled={loading}
                    >
                        {pageSizeOptions.map((size) => (
                            <option key={size} value={size}>
                                {size}
                            </option>
                        ))}
                    </select>
                    <span>entries</span>
                </div>
                <div>
                    Total: <strong>{totalElements}</strong>
                </div>
            </div>

            {loading ? (
                <Loading />
            ) : (
                <>
                    <div className="table-responsive">
                        <table className="table table-bordered table-hover align-middle mb-0 process-queue-table clinic-history-table">
                            <thead>
                                <tr>
                                    <th>
                                        <i className="bi bi-journal-medical"></i> ID
                                    </th>
                                    <th>
                                        <i className="bi bi-cloud-check"></i> Result
                                    </th>
                                    <th>
                                        <i className="bi bi-clock-history"></i> Updated
                                    </th>
                                    <th className="text-end">
                                        <i className="bi bi-gear"></i> Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {historyList.length > 0 ? (
                                    historyList.map((item) => (
                                        <React.Fragment key={item.id}>
                                            <tr className="clinic-history-row">
                                                <td>
                                                    <span className="clinic-history-id">{item.id}</span>
                                                </td>
                                                <td>
                                                    <span className={getStatusBadgeClass(item.result)}>
                                                        {item.result === 'SUCCESS' ? 'Success' : 'Error'}
                                                    </span>
                                                </td>
                                                <td className="clinic-history-date">{item.createdAt}</td>
                                                <td className="text-end">
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary btn-sm"
                                                        onClick={() => toggleRowExpand(item.id)}
                                                        aria-controls={`collapse-notes-${item.id}`}
                                                        aria-expanded={expandedRows[item.id]}
                                                        title="View details"
                                                    >
                                                        {expandedRows[item.id] ? (
                                                            <i className="bi bi-chevron-up"></i>
                                                        ) : (
                                                            <i className="bi bi-chevron-down"></i>
                                                        )}
                                                    </button>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td colSpan="4" className="p-0 border-0">
                                                    <Collapse in={expandedRows[item.id]}>
                                                        <div id={`collapse-notes-${item.id}`} className="clinic-history-detail-panel">
                                                            <div className="row g-3">
                                                                <div className="col-md-6">
                                                                    <div className="notifications-info-row mb-0">
                                                                        <span className="notifications-info-label">Sync ID</span>
                                                                        <div className="notifications-info-value d-flex align-items-center gap-2">
                                                                            <span className="font-monospace small">{item.id}</span>
                                                                            <button
                                                                                type="button"
                                                                                className="clinic-copy-btn"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    copyToClipboard(item.id);
                                                                                }}
                                                                                title="Copy ID"
                                                                            >
                                                                                <i className="bi bi-copy"></i>
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="col-md-6">
                                                                    <div className="notifications-info-row mb-0">
                                                                        <span className="notifications-info-label">PMS Type</span>
                                                                        <span className="notifications-info-value">{item.pmsType || '—'}</span>
                                                                    </div>
                                                                </div>
                                                                <div className="col-md-6">
                                                                    <div className="notifications-info-row mb-0">
                                                                        <span className="notifications-info-label">Result</span>
                                                                        <span className={getStatusBadgeClass(item.result)}>
                                                                            {item.result === 'SUCCESS' ? 'Success' : 'Error'}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <div className="col-md-6">
                                                                    <div className="notifications-info-row mb-0">
                                                                        <span className="notifications-info-label">Updated</span>
                                                                        <span className="notifications-info-value">{item.createdAt}</span>
                                                                    </div>
                                                                </div>
                                                                <div className="col-12">
                                                                    <div className="notifications-info-row mb-0">
                                                                        <span className="notifications-info-label">Notes</span>
                                                                        <pre className="clinic-history-notes">{item.notes || '—'}</pre>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Collapse>
                                                </td>
                                            </tr>
                                        </React.Fragment>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="4" className="p-0 border-0">
                                            <div className="clinic-history-empty">
                                                <i className="bi bi-clock-history"></i>
                                                No sync history records found.
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {historyList.length > 0 && (
                        <div className="clinic-history-footer">
                            <div>
                                Showing {startItem} to {endItem} of {totalElements} entries
                            </div>
                            {totalPages > 1 && (
                                <Pagination className="mb-0">
                                    <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                                    <Pagination.Prev
                                        onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                                        disabled={currentPage === 1}
                                    />
                                    {paginationItems}
                                    <Pagination.Next
                                        onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                                        disabled={currentPage === totalPages}
                                    />
                                    <Pagination.Last
                                        onClick={() => setCurrentPage(totalPages)}
                                        disabled={currentPage === totalPages}
                                    />
                                </Pagination>
                            )}
                        </div>
                    )}
                </>
            )}
        </>
    );
}

export default HistoryTab;
